import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import { FiSettings, FiAlertTriangle, FiCheck, FiBell, FiShield, FiGlobe } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { canAccessFullSettings, getRoleLabel, getRoleBadgeColor } from '../utils/roleCheck';
import i18n from '../i18n/config';

const Settings = () => {
  const { t } = useTranslation();
  const [notification, setNotification] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const { user } = useAuth();

  const hasFullAccess = canAccessFullSettings(user?.role);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;
    setLoadingPrefs(true);
    try {
      const { data, error } = await window.ezsite.apis.tablePage(70673, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'user_id', op: 'Equal', value: user.id }]
      });

      if (!error && data?.List?.[0]) {
        setPreferences(data.List[0]);
      } else {
        // Create default preferences
        const { error: createError } = await window.ezsite.apis.tableCreate(70673, {
          user_id: user.id,
          notify_on_mentions: true,
          notify_property_topics: false,
          notify_building_topics: false,
          notify_topic_replies: true,
          notify_property_faults: false,
          notify_building_faults: false,
          notify_fault_status: true,
          notify_announcements: true,
          email_notifications: false
        });

        if (!createError) {
          await fetchPreferences();
        }
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoadingPrefs(false);
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;
    try {
      const { error } = await window.ezsite.apis.tableUpdate(70673, preferences);
      if (error) throw error;
      toast({ title: t('common.success'), description: t('settings.preferencesSaved') });
    } catch (error) {
      toast({ title: t('common.error'), description: t('settings.preferencesFailed'), variant: 'destructive' });
    }
  };

  const togglePreference = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLanguageChange = async (newLanguage) => {
    if (!preferences) return;

    try {
      // Update in database
      const { error } = await window.ezsite.apis.tableUpdate(70673, {
        ...preferences,
        language: newLanguage
      });

      if (error) throw error;

      // Update local state
      setPreferences((prev) => ({ ...prev, language: newLanguage }));

      // Apply language change immediately
      await i18n.changeLanguage(newLanguage);

      toast({
        title: t('common.success'),
        description: t('settings.languageUpdated') || 'Language updated successfully'
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('settings.languageFailed') || 'Failed to update language',
        variant: 'destructive'
      });
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('settings.title')}</h1>
        <p className="text-gray-600">{t('settings.subtitle')}</p>
      </div>

      {/* Notification */}
      {notification &&
      <div
        className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
        notification.type === 'error' ?
        'bg-red-50 border border-red-200 text-red-800' :
        'bg-green-50 border border-green-200 text-green-800'}`
        }>
          <SafeIcon icon={notification.type === 'error' ? FiAlertTriangle : FiCheck} className="text-lg" />
          <span>{notification.message}</span>
        </div>
      }

      <div className="max-w-2xl space-y-6">
        {/* User Role Info */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <SafeIcon icon={FiShield} className="text-blue-600 text-xl" />
            <h2 className="text-lg font-semibold text-gray-800">{t('settings.roleAndPermissions')}</h2>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('settings.currentRole')}</span>
              <span className={`text-xs font-bold px-3 py-1 rounded-full border ${getRoleBadgeColor(user?.role)}`}>
                {getRoleLabel(user?.role)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              {hasFullAccess ?
              t('settings.fullAccessMessage') :
              t('settings.limitedAccessMessage')}
            </p>
          </div>
        </div>

        {/* Language Preference */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <SafeIcon icon={FiGlobe} />
            <span>{t('settings.languagePreference')}</span>
          </h2>
          {loadingPrefs ?
          <div className="text-center py-4">
              <div className="animate-spin w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            </div> :
          preferences ?
          <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-3">{t('settings.languageDesc')}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                onClick={() => handleLanguageChange('en')}
                className={`p-4 rounded-lg border-2 transition-all ${
                (preferences.language || 'en') === 'en' ?
                'border-blue-600 bg-blue-50 shadow-md' :
                'border-gray-200 bg-white hover:border-blue-300'}`
                }>
                  <div className="text-3xl mb-2">🇬🇧</div>
                  <div className="font-semibold text-gray-800">English</div>
                  {(preferences.language || 'en') === 'en' &&
                <div className="text-xs text-blue-600 mt-1">{t('settings.currentLanguage')}</div>}
                </button>
                <button
                onClick={() => handleLanguageChange('el')}
                className={`p-4 rounded-lg border-2 transition-all ${
                preferences.language === 'el' ?
                'border-blue-600 bg-blue-50 shadow-md' :
                'border-gray-200 bg-white hover:border-blue-300'}`
                }>
                  <div className="text-3xl mb-2">🇬🇷</div>
                  <div className="font-semibold text-gray-800">Ελληνικά</div>
                  {preferences.language === 'el' &&
                <div className="text-xs text-blue-600 mt-1">{t('settings.currentLanguage')}</div>}
                </button>
              </div>
            </div> :
          null}
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <SafeIcon icon={FiBell} />
            <span>{t('settings.notificationPreferences')}</span>
          </h2>
          {loadingPrefs ?
          <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            </div> :
          preferences ?
          <div className="space-y-4">
              <NotificationToggle
              title={t('settings.mentions')}
              description={t('settings.mentionsDesc')}
              checked={preferences.notify_on_mentions}
              onChange={() => togglePreference('notify_on_mentions')} />

              <NotificationToggle
              title={t('settings.propertyTopics')}
              description={t('settings.propertyTopicsDesc')}
              checked={preferences.notify_property_topics}
              onChange={() => togglePreference('notify_property_topics')} />

              <NotificationToggle
              title={t('settings.buildingTopics')}
              description={t('settings.buildingTopicsDesc')}
              checked={preferences.notify_building_topics}
              onChange={() => togglePreference('notify_building_topics')} />

              <NotificationToggle
              title={t('settings.topicReplies')}
              description={t('settings.topicRepliesDesc')}
              checked={preferences.notify_topic_replies}
              onChange={() => togglePreference('notify_topic_replies')} />

              <NotificationToggle
              title={t('settings.propertyFaults')}
              description={t('settings.propertyFaultsDesc')}
              checked={preferences.notify_property_faults}
              onChange={() => togglePreference('notify_property_faults')} />

              <NotificationToggle
              title={t('settings.buildingFaults')}
              description={t('settings.buildingFaultsDesc')}
              checked={preferences.notify_building_faults}
              onChange={() => togglePreference('notify_building_faults')} />

              <NotificationToggle
              title={t('settings.faultStatus')}
              description={t('settings.faultStatusDesc')}
              checked={preferences.notify_fault_status}
              onChange={() => togglePreference('notify_fault_status')} />

              <NotificationToggle
              title={t('settings.announcementsToggle')}
              description={t('settings.announcementsDesc')}
              checked={preferences.notify_announcements}
              onChange={() => togglePreference('notify_announcements')} />

              <div className="border-t border-gray-300 my-4 pt-4">
                <NotificationToggle
                title={t('settings.emailNotifications')}
                description={t('settings.emailNotificationsDesc')}
                checked={preferences.email_notifications}
                onChange={() => togglePreference('email_notifications')} />
              </div>

              <button
              onClick={savePreferences}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors">
                {t('settings.savePreferences')}
              </button>
            </div> :
          null}
        </div>

        {/* About */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('settings.about')}</h2>
          <div className="space-y-3 text-sm text-gray-600">
            <p>{t('settings.version')}</p>
            <p>
              {t('settings.description')}
            </p>
          </div>
        </div>
      </div>
    </div>);

};

function NotificationToggle({ title, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div>
        <h3 className="font-medium text-gray-800">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={`relative w-12 h-6 rounded-full transition ${
        checked ? 'bg-blue-600' : 'bg-gray-300'}`
        }>
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-6' : ''}`
          } />

      </button>
    </div>);

}

export default Settings;